import Button from '@components/Button';
import { PlanCardProps } from '@components/PlanCard';
import { PetPolicies } from '@prisma/client';
import {
  CURRENCY,
  PLAN_BILL_CYCLE,
  PLAN_TYPE,
  PRODUCT_SLUG,
} from '@utils/constant';
import useAxios, { Method } from 'hooks/useAxios';
import { GetStaticProps } from 'next';
import { TFunction, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import AccidentalImg from '@assets/images/accidental_injuries.svg';
import AnyVetImg from '@assets/images/any_vet.svg';
import IllnessImg from '@assets/images/illnesses.svg';
import VetImg from '@assets/images/vet.svg';
import Accordion, { AccordionList } from '@components/Accordion';
import Card from '@components/Card';
import ResetButton from '@components/EligibilityV2/ResetButton';
import Icon from '@components/Icon';
import { useEligibility } from '@contexts/eligibility';
import { ActionType, usePlan } from '@contexts/plan';
import { gtagPushEcomm } from '@utils/ga4';
import { gtm } from '@utils/gtm';
import PlanCarousel from '@components/Plans/PlanCarousel';
import Toggle from '@components/Toggle';
import { resolveLinks, DocumentType } from '@utils/resolveLinks';
import { planImage } from '@utils/helper';

export const reformatPlanName = (value: string | null) => {
  if (!value) return 'planPlus';

  const planName = value.toLowerCase();

  if (planName.includes('basic')) {
    return 'planBasic';
  }

  if (planName.includes('plus')) {
    return 'planPlus';
  }

  if (planName.includes('champion')) {
    return 'planChampion';
  }

  throw new Error('Invalid plan name passed');
};

const limitations = (t: TFunction): { title: string; content: string }[] => [
  {
    title: t('preventative_and_routine_treatments'),
    content: t('preventative_and_routine_treatments_content'),
  },
  {
    title: t('pre-existing_conditions'),
    content: t('pre-existing_conditions_content'),
  },
  {
    title: t('any_illness_during_14day_waiting_period'),
    content: t('any_illness_during_14day_waiting_period_content'),
  },
  {
    title: t('any_second_opinion_treatment_or_fees'),
    content: t('any_second_opinion_treatment_or_fees_content'),
  },
  {
    title: t('removal_of_foreign_object_and_food_poisoning'),
    content: t('removal_of_foreign_object_and_food_poisoning_content'),
  },
  {
    title: t('aids_prosthesis_organ_transplants_surgical_implants'),
    content: t('aids_prosthesis_organ_transplants_surgical_implants_content'),
  },
  {
    title: t('dental_related_treatments'),
    content: t('dental_related_treatments_content'),
  },
];

const Plans: React.FC = () => {
  const router = useRouter();
  const {
    dispatch: planDispatch,
    state: { pets },
  } = usePlan();
  const {
    state: { data },
  } = useEligibility();
  const { t } = useTranslation('planCard');
  const { t: tc } = useTranslation('common');
  const { t: te } = useTranslation('eligibilityV2');
  const [billCycle, setBillCycle] = useState<PLAN_BILL_CYCLE>(
    PLAN_BILL_CYCLE.YEARLY
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pricingParams, setPricingParams] = useState<any>({
    ...pets[0],
    product: 'zurich-takaful',
  });

  const isTakaful = router.asPath.includes('takaful');

  const {
    status,
    exec: getPolicies,
    data: { policies },
  } = useAxios<{ policies: PetPolicies[] }>({
    url: `/api/policies?${new URLSearchParams(pricingParams).toString()}`,
    method: Method.Get,
  });
  const illnessFeatures = [
    [t('fever_flu'), true],
    [t('skin_ear_eye'), true],
    [t('viral'), true],
    [t('influenza'), true],
  ] as Array<[string, boolean]>;

  const accidentFeatures = [
    [t('fractures'), true],
    [t('fights'), true],
    [t('limbs'), true],
  ] as Array<[string, boolean]>;

  const featureLine = (description: string, supported: boolean) => (
    <div
      className="flex flex-row items-center py-1 text-left align-middle"
      key={description}
    >
      {supported && (
        <Icon
          name="tick"
          color="green"
          size={1.3}
          className="float-left mr-2"
        />
      )}
      <span className="description">{description}</span>
    </div>
  );

  useEffect(() => {
    if (pets && pets.length > 0) {
      // prepare pricing params
      setPricingParams(
        (prev: any) =>
          ({
            ...(prev as any),
            ...pets[0],
            product: 'zurich-takaful',
          } as any)
      );

      // fetch policies only after pricing params are set
      getPolicies();
    }
    gtm.track('compare_plans', {
      source: 'signup-lite',
    });
  }, [pets]);

  const availablePlans: PlanCardProps & { id: string }[] = policies
    ? policies
        .filter((item: any) => item.petType === data.petType)
        .map((item: any) => ({
          id: item.id,
          plan: item.name,
          image: planImage(item.name),
          yearlyPrice: item.yearlyPrice / 100,
          monthlyPrice: item.monthlyPrice / 100,
          medicalCost: item.vetMedicalLimit / 100,
          overnightStay: item.maxClinicOvernightStay,
          followUpTreatment: item.followupTreatmentDays,
          thirdPartyDamages: item.thirdPartyLimit / 100,
          cremationCost: item.burialLimit / 100,
          freeMicroChip: item.freeMicroChip,
          deductiblesAmount: item.deductiblesAmount,
          billCycle: billCycle,
        }))
    : [];

  const defaultPlan = availablePlans?.find((plan: any) => {
    return plan.plan.toLowerCase() === 'plus yearly takaful';
  });

  const [selected, selectPlan] = useState<any | undefined>(defaultPlan);

  useEffect(() => {
    if (typeof selected === 'undefined') {
      selectPlan(defaultPlan);
    }
  }, [policies]);

  useEffect(() => {
    if (availablePlans && availablePlans.length > 0) {
      const items = availablePlans.map((item: any, index) => ({
        item_id: item.id,
        item_name: item.plan,
        price:
          billCycle === PLAN_BILL_CYCLE.MONTHLY
            ? item.monthlyPrice
            : item.yearlyPrice,
        item_category: data.petType,
        item_list_name: billCycle,
        index,
      }));
      gtagPushEcomm('view_item_list', { items });
    }
  }, [billCycle, policies]);

  const handleSubmit = () => {
    setIsLoading(true);
    gtm.ecommerce('plan_type', {
      currency: CURRENCY,
      value: selected?.yearlyPrice,
      plan_type: selected?.plan?.split(' ')?.pop(),
      plan_name: selected?.plan,
      quantity: 1,
      source: 'signup-lite',
    });

    gtagPushEcomm('add_to_cart', {
      items: [
        {
          item_id: selected?.id,
          item_name: selected?.plan,
          price:
            billCycle === PLAN_BILL_CYCLE.MONTHLY
              ? selected?.monthlyPrice
              : selected?.yearlyPrice,
          item_category: data.petType,
        },
      ],
      currency: CURRENCY,
    });

    if (pets.length === 0) {
      planDispatch({
        type: ActionType.SET_POLICY,
        payload: {
          policy: selected,
        },
      });
    } else {
      // Should not update all pets with the same policy, only the first one
      planDispatch({
        type: ActionType.SET_POLICY,
        payload: {
          policy: selected,
          id: pets[0].id,
        },
      });
    }

    router.push({ pathname: '/eligibility/takaful/healthDetails' });
  };

  const downloadFile = async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = url.split('/').pop() || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback to opening in new window if download fails
      window.open(url, '_blank');
    }
  };

  return (
    <div className="flex flex-col justify-center items-center p-2 pt-4 w-full align-middle bg-white">
      <div className="w-full space-y-4 md:w-[500px]">
        <div className="space-y-2 text-center">
          <p className="text-xl font-bold text-gray-700">
            {te('plans.pawfect_is_eligibile', {
              petName: data.petName ?? te('pet_microchip_furkid'),
            })}
          </p>
          <p className="text-sm text-gray-500">{te('plans.cancel_anytime')}</p>
        </div>
        {process.env.NEXT_PUBLIC_ENABLE_TAKAFUL_MONTHLY === 'true' && (
          <Toggle
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              e.target.checked
                ? setBillCycle(PLAN_BILL_CYCLE.MONTHLY)
                : setBillCycle(PLAN_BILL_CYCLE.YEARLY)
            }
            billCycle={billCycle}
          />
        )}
        <section>
          <PlanCarousel
            currentStatus={status}
            purchaseType={PLAN_TYPE.PLAN}
            handleRetry={getPolicies}
            handleSelect={(planData) => {
              gtm.track('compare_plans_interacted', {
                source: 'signup-lite',
              });
              selectPlan({ billCycle: billCycle, ...planData });
              gtagPushEcomm('view_item', {
                items: [
                  {
                    item_id: planData.id,
                    item_name: planData.plan,
                    price:
                      billCycle === PLAN_BILL_CYCLE.MONTHLY
                        ? planData.monthlyPrice
                        : planData.yearlyPrice,
                    item_category: data.petType,
                  },
                ],
                currency: CURRENCY,
              });
            }}
            plans={availablePlans as any[]}
            billCycle={billCycle}
            selectedPlanId={selected?.id}
          />
          <div className="flex-grow px-4 pb-4 mt-4 text-center text-gray-500">
            <div className="mb-4">{t('policy_term_apply')}</div>
            {isTakaful && (
              <div className="mb-4">{t('underwritten_by_zurich')}</div>
            )}

            <div>{t('learn_benefits')}</div>
            <div className="inline-block mx-auto mt-2">
              <Icon name="mouse" />
            </div>
            <ResetButton flow={'takaful'} />
          </div>
        </section>
      </div>
      <section className="flex flex-col justify-center items-center px-4 py-8 w-full text-center align-middle bg-primary-50">
        <div className="mb-4">
          <h2 className="mb-4 text-2xl font-bold">{t('how_it_will_work')}</h2>
          <div className="md:w-[850px] sm:w-full mx-auto grid sm:grid-cols-1 md:grid-cols-2 gap-8">
            <div className="mb-4">
              <div className="h-[63px] w-[55px] mx-auto mb-2">
                <Image src={VetImg} alt="vet" />
              </div>
              <h3 className="mb-2 text-xl font-bold">{t('we_pay_you')}</h3>
              <div className="text-lg">{t('claimable_items')}</div>
            </div>

            <div className="mb-4">
              <div className="h-[63px] w-[77px] mx-auto mb-2">
                <Image src={AnyVetImg} alt="Any vet" />
              </div>

              <h3 className="mb-2 text-xl font-bold">{t('visit_any_vet')}</h3>
              <div className="text-lg">
                {t('bring_us_the_receipt_after')}{' '}
                <span className="font-bold">{t('get_paid_after')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-2">
          <h2 className="mb-4 text-2xl font-bold">
            {t('illness_and_injury_we_cover')}
          </h2>
          <div className="md:w-[850px] sm:w-full mx-auto grid sm:grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <div className="h-[63px] w-[41px] mx-auto mb-2">
                <Image src={IllnessImg} alt="Illnesses" />
              </div>

              <h3 className="mb-2 text-xl font-bold">{t('illnesses')}</h3>
              <div className="text-lg">{t('illnesses_2')}</div>

              <Card border noShadow className="mt-4 text-lg bg-white">
                {illnessFeatures.map(([description, supported]) =>
                  featureLine(description, supported)
                )}
              </Card>
            </div>

            <div className="mb-4">
              <div className="h-[63px] w-[64px] mx-auto mb-2 ">
                <Image src={AccidentalImg} alt="Accidental Injuries" />
              </div>

              <h3 className="mb-2 text-xl font-bold">
                {t('accidental_injuries')}
              </h3>
              <div className="text-lg">{t('accidental_injuries_2')}</div>

              <Card border noShadow className="mt-4 text-lg bg-white">
                {accidentFeatures.map(([description, supported]) =>
                  featureLine(description, supported)
                )}
              </Card>
            </div>
          </div>
        </div>

        <div className="flex-grow px-4 py-2 text-lg text-center">
          <div className="font-bold">{t('and_many_more')}</div>
          <div className="inline-block mx-auto mt-2">
            <a
              className="link"
              onClick={(e) => {
                e.preventDefault();
                downloadFile(
                  resolveLinks(
                    'my',
                    PRODUCT_SLUG.ZURICH_TAKAFUL,
                    DocumentType.CW
                  )
                );
              }}
              rel="noreferrer"
            >
              {t('check_out_our_policy_wording')}
            </a>
          </div>
        </div>
      </section>

      <section className="flex flex-col justify-center items-center px-4 py-8 w-full text-lg align-middle bg-gray-50">
        <div className="md:w-[850px] mx-auto ">
          <h2 className="mb-4 text-2xl font-bold text-center">
            {t('what_we_dont_cover')}
          </h2>
          <div className="md:w-[850px] sm:w-full mx-auto mb-4 text-center">
            {t('what_we_dont_cover_content')}
          </div>
          <div className="container flex flex-col gap-8 mx-auto md:flex-row">
            <AccordionList className="flex-grow">
              {limitations(t).map(
                (limitation: { title: string; content: string }) => (
                  <Accordion key={limitation.title} title={limitation.title}>
                    {limitation.content}
                  </Accordion>
                )
              )}
            </AccordionList>
          </div>
        </div>
      </section>
      <footer className="flex-grow-0 px-4 w-full actions">
        <div className="flex flex-grow gap-4 mx-auto max-w-5xl">
          <div className="grid grid-cols-1">
            {!!selected && (
              <div className="text-gray-500">
                {t(reformatPlanName(selected.plan), {
                  petType: 'Kucing',
                }) + ' Takaful'}
              </div>
            )}
            {!!selected && (
              <div className="font-bold">
                RM{' '}
                {billCycle === PLAN_BILL_CYCLE.MONTHLY
                  ? selected.monthlyPrice
                  : selected.yearlyPrice}
                /
                {billCycle === PLAN_BILL_CYCLE.YEARLY
                  ? tc('year')
                  : tc('month')}
              </div>
            )}
            {!selected && (
              <a className="self-center link" href="#plan">
                {t('select_a_plan')}
              </a>
            )}
          </div>
          <Button
            className="flex-grow justify-between mt-auto"
            disabled={!selected || isLoading}
            onClick={handleSubmit}
          >
            <span />
            {isLoading ? (
              '...'
            ) : (
              <>
                <div>{tc('next')}</div>
                <Icon name="arrow-right" />
              </>
            )}
          </Button>
        </div>
      </footer>
    </div>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => ({
  props: {
    ...(await serverSideTranslations(locale, [
      'dashboard',
      'planCard',
      'common',
      'eligibilityV2',
    ])),
  },
});

export default Plans;
